import { gql } from '@apollo/client';
import { Box, Chip, Divider, Typography } from '@mui/material';
import { IconLinearMonitorMobile } from 'components/icons/components/linear/IconLinearMonitorMobile';
import { IconLinearSort } from 'components/icons/components/linear/IconLinearSort';
import {
  NestedFiltersMenuItemBaseValueType,
  NestedFiltersOptionType,
} from 'features/nestedFilters/components';
import { NestedFiltersMenuView } from 'features/nestedFilters/views/menu';
import { platformLabelMap } from 'features/socialPost/constants';
import {
  Platform,
  SimpleDateRangeFilterInput,
  useGetCommunityGraphDataForEmergingCommunitiesQuery,
} from 'graphql/generated';
import moment from 'moment';
import { useMemo, useState } from 'react';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { theme } from 'styles/theme';
import { CreatePortalById } from 'utils/helpers';
import { formatBigNumber } from 'utils/number';
import { SocialListeningEmergingCommunityTable } from './SocialListeningEmergingCommunityTable';

enum GraphSortField {
  PostCount = 'postCount',
  Engagement = 'engagement',
  ViewCount = 'viewCount',
}

const GraphSortFieldLabelMap = {
  [GraphSortField.PostCount]: 'Total post count',
  [GraphSortField.Engagement]: 'Total engagement',
  [GraphSortField.ViewCount]: 'Total view count',
};

const STATIC_POINTS = Array.from({ length: 6 }, (_, index) => ({
  date: moment().subtract(index, 'days').format('YYYY-MM-DD'),
  value: 0,
}));

const Y_AXIS_TICKS = [0, 100000, 200000, 300000, 400000];

// eslint-disable-next-line
gql`
  query GetCommunityGraphDataForEmergingCommunities(
    $input: GetCommunityGraphDataArgs!
  ) {
    getCommunityGraphData(input: $input) {
      community
      date
      postCount
      totalEngagement
      totalViews
    }
  }
`;

interface SocialListeningEmergingCommunitiesProps {
  brandId: string;
  selectedDateRange: SimpleDateRangeFilterInput;
}

const CommunityColorMap = [
  {
    backgroundColor: 'rgba(230, 221, 237, 0.50)',
    color: theme.colors?.utility['purple-3'],
  },
  {
    backgroundColor: 'rgba(217, 227, 243, 0.50)',
    color: theme.colors?.utility['blue-3'],
  },
  {
    backgroundColor: 'rgba(248, 232, 202, 0.50)',
    color: theme.colors?.utility['yellow-3'],
  },
  {
    backgroundColor: 'rgba(224, 228, 202, 0.50)',
    color: theme.colors?.utility['green-3'],
  },
  {
    backgroundColor: 'rgba(246, 229, 216, 0.50)',
    color: theme.colors?.utility['orange-3'],
  },
];

export const SocialListeningEmergingCommunities = ({
  selectedDateRange,
  brandId,
}: SocialListeningEmergingCommunitiesProps) => {
  const [selectedPlatforms, setSelectedPlatforms] = useState<Platform[]>([]);
  const [selectedGraphSort, setSelectedGraphSort] = useState<GraphSortField>(
    GraphSortField.PostCount,
  );
  const [selectedCommunities, setSelectedCommunities] = useState<string[]>([]);

  const { data: communitiesGraphData, loading: communitiesGraphDataLoading } =
    useGetCommunityGraphDataForEmergingCommunitiesQuery({
      variables: {
        input: {
          filters: {
            brandId,
            communities: selectedCommunities,
            dateRange: selectedDateRange,
            platforms: selectedPlatforms,
          },
        },
      },
      skip: !selectedCommunities.length,
    });

  const selectedCommunitiesData = useMemo(() => {
    return selectedCommunities.map((item, index) => {
      const randomIndex =
        index % CommunityColorMap.length === 0
          ? 0
          : index % CommunityColorMap.length;

      return {
        community: item,
        color: CommunityColorMap[randomIndex].color,
        backgroundColor: CommunityColorMap[randomIndex].backgroundColor,
      };
    });
  }, [selectedCommunities]);

  // Process data for the chart
  const processedData = useMemo(() => {
    if (!communitiesGraphData?.getCommunityGraphData) return [];

    const data = communitiesGraphData.getCommunityGraphData;

    // Get all unique dates and communities
    const allDates = Array.from(new Set(data.map((item) => item.date))).sort();
    const allCommunities = Array.from(
      new Set(data.map((item) => item.community)),
    );

    // Create a complete dataset with all dates
    return allDates.map((date) => {
      // Start with the date
      const dateEntry: Record<string, any> = { date };

      // For each community, find its value for this date or use 0
      allCommunities.forEach((community) => {
        const dataPoint = data.find(
          (item) => item.date === date && item.community === community,
        );

        let value = 0;
        if (dataPoint) {
          switch (selectedGraphSort) {
            case GraphSortField.Engagement:
              value = dataPoint.totalEngagement;
              break;
            case GraphSortField.ViewCount:
              value = dataPoint.totalViews;
              break;
            default:
              value = dataPoint.postCount;
          }
        }

        dateEntry[community] = value;
      });

      return dateEntry;
    });
  }, [communitiesGraphData, selectedGraphSort]);

  const hasData = processedData.length > 0;

  return (
    <>
      <CreatePortalById containerId="community-dialog-filters">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 4,
          }}
        >
          <NestedFiltersMenuView
            values={{
              platforms: {
                value: selectedPlatforms.map((platform) => ({
                  label: platformLabelMap[platform],
                  value: platform,
                })),
              },
            }}
            componentsProps={{
              trigger: {
                text:
                  selectedPlatforms.length > 0
                    ? selectedPlatforms
                        .map((p) => platformLabelMap[p])
                        .join(' & ')
                    : 'Instagram & TikTok',
                Icon: IconLinearMonitorMobile,
                showChevronIcon: true,
                sx: {
                  background: theme.colors?.primary.parchment,
                },
              },
              menu: {
                sx: {
                  background: theme.colors?.primary.parchment,
                },
              },
            }}
            items={[
              {
                type: 'multi-select',
                key: 'platforms',
                options: [Platform.Instagram, Platform.Tiktok].map(
                  (platform) => ({
                    label: platformLabelMap[platform],
                    value: platform,
                  }),
                ),
              },
            ]}
            onChange={(
              values: Record<
                string,
                NestedFiltersMenuItemBaseValueType<unknown>
              >,
            ) => {
              const platforms = (
                (values.platforms?.value || []) as NestedFiltersOptionType[]
              ).map((v) => v.value as Platform);
              setSelectedPlatforms(platforms);
            }}
          />

          <NestedFiltersMenuView
            values={{
              sort: {
                value: {
                  label: GraphSortFieldLabelMap[selectedGraphSort],
                  value: selectedGraphSort,
                },
              },
            }}
            componentsProps={{
              trigger: {
                text: GraphSortFieldLabelMap[selectedGraphSort],
                Icon: IconLinearSort,
                showChevronIcon: true,
                sx: {
                  background: theme.colors?.primary.parchment,
                },
              },
              menu: {
                sx: {
                  background: theme.colors?.primary.parchment,
                },
              },
            }}
            items={[
              {
                type: 'single-select',
                key: 'sort',
                options: Object.keys(GraphSortField).map((key) => {
                  const sort = key as keyof typeof GraphSortField;
                  const value = GraphSortField[sort];
                  return {
                    label: GraphSortFieldLabelMap[value],
                    value,
                  };
                }),
              },
            ]}
            onChange={(
              values: Record<
                string,
                NestedFiltersMenuItemBaseValueType<unknown>
              >,
            ) => {
              const sort = values.sort?.value as NestedFiltersOptionType;
              setSelectedGraphSort(sort.value as GraphSortField);
            }}
          />
        </Box>
      </CreatePortalById>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          borderRadius: 6,
          border: `1px solid ${theme.colors?.utility[300]}`,
          background: theme.colors?.primary.parchment,
          boxShadow:
            '0px 8px 28px -6px rgba(24, 39, 75, 0.03), 0px 18px 88px -4px rgba(24, 39, 75, 0.06)',
          p: 6,
        }}
      >
        <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
          {selectedCommunitiesData.map((community) => (
            <Chip
              key={community.community}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      height: 6,
                      width: 6,
                      background: community.color,
                      borderRadius: 100,
                    }}
                  />
                  <Typography
                    variant="headline-xs"
                    sx={{
                      color: community.color,
                    }}
                  >
                    {community.community}
                  </Typography>
                </Box>
              }
              sx={{
                backgroundColor: community.backgroundColor,
                border: 'none',
                height: 26,
                svg: {
                  height: 16,
                  width: 16,
                  color: `${community.color} !important`,
                  opacity: 0.6,
                },
              }}
              onDelete={() => {
                setSelectedCommunities(
                  selectedCommunities.filter((c) => c !== community.community),
                );
              }}
            />
          ))}
        </Box>

        {/* No graph Data View */}
        <Box sx={{ width: '100%', height: 300, position: 'relative', mx: -6 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart
              data={hasData ? processedData : STATIC_POINTS}
              margin={{
                top: 8,
                right: 30,
                left: 20,
                bottom: 0,
              }}
            >
              {hasData && (
                <>
                  <CartesianGrid
                    strokeDasharray="4 4"
                    stroke={theme.colors?.utility[400]}
                    vertical={false}
                    horizontal
                  />
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload, label }) => {
                      if (!active || !payload || !payload.length) return null;
                      return (
                        <Box
                          sx={{
                            background: theme.colors?.primary.white,
                            border: `1px solid ${theme.colors?.utility[300]}`,
                            borderRadius: 3,
                            boxShadow: '0px 3px 12px -3px rgba(0, 0, 0, 0.16)',
                            p: 4,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 3,
                          }}
                        >
                          <Typography
                            variant="headline-sm"
                            sx={{
                              color: theme.colors?.utility[900],
                            }}
                          >
                            {moment(label).format('ddd, D MMM YYYY')}
                          </Typography>
                          {payload.map((entry) => {
                            const communityData = selectedCommunitiesData.find(
                              (c) => c.community === entry.name,
                            );
                            return (
                              <Box
                                key={entry.name}
                                sx={{
                                  color:
                                    communityData?.color ||
                                    theme.colors?.utility[800],
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'inline-block',
                                    width: 10,
                                    height: 10,
                                    borderRadius: '50%',
                                    background:
                                      communityData?.color ||
                                      theme.colors?.utility[800],
                                  }}
                                />
                                <Typography variant="headline-xs">
                                  {entry.name}:
                                </Typography>
                                <Typography variant="headline-xs">
                                  {formatBigNumber(Number(entry.value) ?? 0)}
                                </Typography>
                              </Box>
                            );
                          })}
                        </Box>
                      );
                    }}
                  />
                </>
              )}
              <XAxis
                dataKey="date"
                tickFormatter={(value) =>
                  moment(value).format('ddd, D MMM YYYY')
                }
                axisLine={false}
                tickLine={false}
                stroke={theme.colors?.utility[600]}
                fontSize={theme.typography['headline-sm'].fontSize}
                fontWeight={theme.typography['headline-sm'].fontWeight}
                fontFamily={theme.typography['headline-sm'].fontFamily}
                dy={5}
                padding={{ left: 60, right: 40 }}
                minTickGap={4}
              />
              <YAxis
                tickFormatter={(value) => formatBigNumber(value)}
                axisLine={false}
                tickLine={false}
                stroke={theme.colors?.utility[600]}
                fontSize={theme.typography['headline-sm'].fontSize}
                fontWeight={theme.typography['headline-sm'].fontWeight}
                fontFamily={theme.typography['headline-sm'].fontFamily}
                dx={-10}
                domain={[0, 'auto']}
                {...(!hasData && {
                  ticks: Y_AXIS_TICKS,
                  domain: [0, 400000],
                  allowDataOverflow: true,
                })}
              />
              {hasData &&
                selectedCommunitiesData.map((community) => (
                  <Line
                    key={community.community}
                    type="monotone"
                    dataKey={community.community}
                    stroke={community.color}
                    strokeWidth={3}
                    dot={false}
                    strokeLinecap="round"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>

          {!hasData && !communitiesGraphDataLoading && (
            <Box
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                textAlign: 'center',
                background: 'rgba(35, 6, 3, 0.03)',
                borderRadius: 3,
                p: 6,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 3,
              }}
            >
              <Typography variant="headline-lg">😶</Typography>
              <Typography
                variant="headline-sm"
                color={theme.colors?.utility[700]}
              >
                It's giving... nothing. Pick a community to change that.
              </Typography>
            </Box>
          )}
        </Box>

        <Divider sx={{ mx: -6, border: '1px solid rgba(35, 6, 3, 0.07)' }} />
        <SocialListeningEmergingCommunityTable
          brandId={brandId}
          dateRange={selectedDateRange}
          platforms={selectedPlatforms}
          selectedCommunities={selectedCommunities}
          setSelectedCommunities={setSelectedCommunities}
        />
      </Box>
    </>
  );
};
